import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Compass, Sparkles } from 'lucide-react';

/**
 * Adventures page body (iter 145, Section 2) - city-based organisation.
 *
 * Shared between the public marketing surface (`/adventures`) and the
 * portal surface (`/portal/adventures`). Renders 9 city cards. Each
 * click opens the corresponding Viator partner-shop link in a new tab.
 *
 * `surface` is passed so click tracking can distinguish where each
 * click originated (`adventures_public` vs `adventures_portal`).
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const trackClick = ({ surface, city, userRole }) => {
  try {
    fetch(`${API_URL}/api/affiliates/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        surface,
        city_name: city || null,
        user_role: userRole || null,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
};

// Cloudinary city thumbnails - same permanent library used across the
// site (Calculator results, City Guides). 1200x800 c_fill, q_auto.
const CITY_IMG = (slug) =>
  `https://res.cloudinary.com/des9fk8et/image/upload/w_1200,h_800,c_fill,q_auto,f_auto/mundalo/cities/${slug}.jpg`;

const CITIES = [
  {
    name: 'Mexico City',
    slug: 'mexico-city',
    img: CITY_IMG('mexico-city'),
    blurb: 'Lucha libre nights, Xochimilco at dawn, Frida\'s house.',
    url: 'https://www.viator.com/partner-shop/mundalo/Mexico-City-2026?medium=link&medium_version=shop&campaign=MexicoCity',
  },
  {
    name: 'Mérida',
    slug: 'merida',
    img: CITY_IMG('merida'),
    blurb: 'Cenote swims, hacienda dinners, colonial street markets.',
    url: 'https://www.viator.com/partner-shop/mundalo/Merida-2026?medium=link&medium_version=shop&campaign=Merida2026',
  },
  {
    name: 'Oaxaca',
    slug: 'oaxaca-city',
    img: CITY_IMG('oaxaca-city'),
    blurb: 'Monte Albán ruins, mezcal tastings, Mole cooking classes.',
    url: 'https://www.viator.com/partner-shop/mundalo/Oaxaca-2026?medium=link&medium_version=shop&campaign=Oaxaca2026',
  },
  {
    name: 'Puerto Vallarta',
    slug: 'puerto-vallarta',
    img: CITY_IMG('puerto-vallarta'),
    blurb: 'Sunset sails, jungle zip lines, Pacific snorkeling.',
    url: 'https://www.viator.com/partner-shop/mundalo/Puerto-Vallarta-2026?medium=link&medium_version=shop&campaign=Puertovallarta2026',
  },
  {
    name: 'Guadalajara',
    slug: 'guadalajara',
    img: CITY_IMG('guadalajara'),
    blurb: 'Tequila town tours, mariachi shows, lakeside escapes.',
    url: 'https://www.viator.com/partner-shop/mundalo/Guadalajara-2026?medium=link&medium_version=shop&campaign=Guadalajara2026',
  },
  {
    name: 'San Miguel de Allende',
    slug: 'san-miguel-de-allende',
    img: CITY_IMG('san-miguel-de-allende'),
    blurb: 'Hot springs, vineyard lunches, art-walk afternoons.',
    url: 'https://www.viator.com/partner-shop/mundalo/San-Miguel-de-Allende-2026?medium=link&medium_version=shop&campaign=SanMiguel',
  },
  {
    name: 'Monterrey',
    slug: 'monterrey',
    img: CITY_IMG('monterrey'),
    blurb: 'Sierra Madre hikes, Barrio Antiguo nights, canyon adventures.',
    url: 'https://www.viator.com/partner-shop/mundalo/Monterrey-2026?medium=link&medium_version=shop&campaign=Monterrey',
  },
  {
    name: 'Cancún',
    slug: 'cancun',
    // Cancún not yet uploaded to Cloudinary mundalo/cities/ - use verified Unsplash
    // beach shot as fallback (curated Carribean turquoise + palms).
    img: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=1200&auto=format&fit=crop&q=80',
    blurb: 'Cenote dives, Tulum day trips, reef snorkeling.',
    url: 'https://www.viator.com/partner-shop/mundalo/Cancun-2026?medium=link&medium_version=shop&campaign=Cancun',
  },
  {
    name: 'Cabo San Lucas',
    slug: 'cabo-san-lucas',
    img: CITY_IMG('cabo-san-lucas'),
    blurb: 'Arch boat tours, sportfishing charters, desert ATV rides.',
    url: 'https://www.viator.com/partner-shop/mundalo/Cabo-San-Lucas-2026?medium=link&medium_version=shop&campaign=CaboSanLucas',
  },
];

const CityCard = ({ c, surface, userRole }) => (
  <a
    href={c.url}
    target="_blank"
    rel="noopener noreferrer sponsored"
    onClick={() => trackClick({ surface, city: c.name, userRole })}
    className="group block bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden hover:border-[#2CA8A0]/55 hover:shadow-sm transition"
    data-testid={`adventure-city-${c.slug}`}
  >
    <div className="aspect-[16/10] bg-[#F4ECD8] overflow-hidden relative">
      <img
        src={c.img}
        alt={c.name}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F2E]/55 via-transparent to-transparent" />
      <div className="absolute bottom-3 left-4 right-4">
        <h3 className="font-display italic text-2xl md:text-3xl text-white leading-tight" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.45)' }}>{c.name}</h3>
      </div>
    </div>
    <div className="p-5 flex items-start justify-between gap-4">
      <p className="text-sm text-[#0A1F2E]/75 leading-relaxed flex-1">{c.blurb}</p>
      <span
        className="flex-none mt-0.5 inline-flex items-center gap-1 text-[#1A5C6E] font-mono uppercase text-[10px] tracking-[0.18em] group-hover:text-[#2CA8A0] transition-colors"
        data-testid={`adventure-city-cta-${c.slug}`}
      >
        Explore <ArrowUpRight className="w-3 h-3" />
      </span>
    </div>
  </a>
);

const AdventuresContent = ({ surface, userRole }) => (
  <div data-testid="adventures-content">
    {/* Hero */}
    <section className="relative isolate overflow-hidden" data-testid="adventures-hero">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=2200&auto=format&fit=crop&q=80"
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-[#0A1F2E]/45" />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top right, rgba(10,31,46,0.78) 0%, rgba(10,31,46,0.45) 45%, rgba(10,31,46,0.18) 100%)',
          }}
        />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <p className="font-mono uppercase text-[11px] tracking-[0.25em] text-[#2CA8A0] mb-4" data-testid="adventures-hero-eyebrow">
          Adventures · Mexico
        </p>
        <h1
          className="font-display italic text-5xl md:text-7xl leading-[1.02] mb-5 max-w-3xl text-white"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          data-testid="adventures-hero-headline"
        >
          Pick a city. Pick your day.
        </h1>
        <p
          className="font-body text-lg md:text-xl text-[#F9F6EF] max-w-2xl leading-relaxed"
          style={{ textShadow: '0 1px 4px rgba(0,0,0,0.35)' }}
          data-testid="adventures-hero-subtext"
        >
          Curated experiences in nine Mexican cities - tours, food, water, ruins, all of it. Tap a city.
        </p>
      </div>
    </section>

    {/* iter 187.o - Parallel Mundalo Community CTA. Adjacent to
        (not blocking) the Viator city grid. Compliant with Viator's
        Partner Program terms which prohibit gating or redirecting the
        outbound affiliate click; a separate signup pitch on the same
        page is permitted. Non-subscribers sign up for member-only
        experiences here; subscribers already inside the portal see
        the portal-side variant. */}
    {surface === 'adventures_public' && (
      <section
        className="bg-[#0A1F2E] py-14 md:py-16"
        data-testid="adventures-community-cta"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-[#E8A830]" aria-hidden />
            <p className="font-mono uppercase text-[11px] tracking-[0.24em] text-[#E8A830]">
              Members only
            </p>
          </div>
          <h2 className="font-display italic text-3xl md:text-5xl text-[#F4ECD8] leading-[1.05] mb-4 max-w-2xl">
            Mundalo Community Adventures
          </h2>
          <p className="font-body text-base md:text-lg text-[#F4ECD8]/80 max-w-2xl mb-6 leading-relaxed">
            Small-group experiences curated by Mundalo - private mezcal tastings, host-family stays, our founder&apos;s Mexico City food-crawl. Available only to Community members.
          </p>
          <Link
            to="/start"
            data-testid="adventures-community-signup-cta"
            className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold font-mono uppercase text-xs tracking-[0.18em] px-5 py-3 rounded-full transition-all hover:-translate-y-px shadow-sm"
          >
            Join Community <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    )}

    {/* City grid - 9 cards */}
    <section className="bg-[#F9F6EF] py-16 md:py-20" data-testid="adventures-cities-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          data-testid="adventures-cities-grid"
        >
          {CITIES.map((c) => (
            <CityCard key={c.slug} c={c} surface={surface} userRole={userRole} />
          ))}
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="bg-white py-16 md:py-20" data-testid="adventures-bottom-cta">
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#2CA8A0]/12 flex items-center justify-center mb-5">
          <Compass className="w-7 h-7 text-[#1A5C6E]" />
        </div>
        <h2 className="font-display italic text-3xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-4">
          Pick your city. Pick your day.
        </h2>
        <p className="font-body text-base md:text-lg text-[#0A1F2E]/70 max-w-xl mx-auto">
          Curated through our Viator partner shop - every booking supports Mundalo.
        </p>
      </div>
    </section>
  </div>
);

export default AdventuresContent;
